/**
 * Manifiestos: la lista de trabajo del logístico.
 *
 * 🚨 La pidió Luis:
 *
 *   «La idea es usar esta plataforma para que los logísticos le agreguen los
 *    manifiestos a cada uno de los viajes. Cuando un conductor ya emprende el
 *    viaje, el sistema tiene que alertar porque HAY MULTAS si no tienen el
 *    manifiesto.»
 *
 * Esta pantalla es la otra mitad de eso: sin un sitio donde subirlos, el aviso
 * de la app del conductor sería un reclamo sin salida.
 *
 * 🚨 Muestra lo que FALTA, no todo. Un listado de 5.000 manifiestos con los que
 * ya están hechos no es una herramienta de trabajo: lo que el logístico necesita
 * saber es a cuáles les falta y cuál sale primero.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { pedirConSesion, tokenGuardado } from './Entrar.tsx'
import { usarPaginas, PieDeTabla } from './Tabla.tsx'
import { puedeCambiar } from './Entrar.tsx'
import { fmt } from './formato.ts'
import { estaEnLaCalle } from '@symtrack/shared'

/** Un archivo escogido y a qué manifiesto se propuso. */
type Propuesta = {
  archivo: File
  manifiesto: string | null
  porque: 'nombre' | 'ambiguo' | 'sin-coincidencia'
  candidatos?: string[]
  estado: 'listo' | 'subiendo' | 'subido' | 'error'
  mensaje?: string
}

type Subido = {
  numero_manifiesto: string
  bytes: number
  mime: string
  subido_at: string
  subido_por: string
  placas: string | null
  conductores: string | null
  remesas: number
  /** Lo dice el servidor con el mismo mínimo que usa la subida. */
  sirve?: boolean
}

type Faltante = {
  /** ¿El conductor ya arrancó? Entonces va rodando SIN el papel: multa. */
  ya_arranco?: boolean
  /** Sigue en la calle: arrancar no basta, la carga puede estar entregada. */
  sigue_en_calle?: boolean
  /** ¿Algún conductor lo ve todavía, con la fecha de corte puesta? */
  lo_ve_el_conductor?: boolean
  dias_esperando?: number | string | null
  estado_vulcano?: string | null
  estado_evidencia?: string | null
  numero_manifiesto: string
  remesas: number
  placas: string | null
  conductores: string | null
  fecha_despacho: string | null
}

/** Los atajos de fecha, cada uno diciendo qué fechas aplica. */
function atajosManifiesto(): { texto: string; cuando: string; desde: string; hasta: string }[] {
  const iso = (d: Date) => d.toLocaleDateString('sv-SE')
  const corto = (d: Date) =>
    `${d.getDate()} ${d.toLocaleDateString('es-CO', { month: 'short' }).replace('.', '')}`
  const hoy = new Date()
  const ayer = new Date(hoy); ayer.setDate(hoy.getDate() - 1)
  const lunes = new Date(hoy); lunes.setDate(hoy.getDate() - ((hoy.getDay() + 6) % 7))
  const mes = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
  return [
    { texto: 'Hoy', cuando: corto(hoy), desde: iso(hoy), hasta: iso(hoy) },
    { texto: 'Ayer', cuando: corto(ayer), desde: iso(ayer), hasta: iso(ayer) },
    { texto: 'Esta semana', cuando: `${corto(lunes)} – ${corto(hoy)}`, desde: iso(lunes), hasta: iso(hoy) },
    { texto: 'Este mes', cuando: `${corto(mes)} – ${corto(hoy)}`, desde: iso(mes), hasta: iso(hoy) },
  ]
}

/*
 * ⛔⛔ DOS EJES DE ESTADO, SEPARADOS. Regla de Luis:
 *
 *   «Hay varios estados: unos son los que te da Vulcano y otros son los que
 *    nosotros tenemos en esta app. Esta app debe manejar la ÚLTIMA MILLA que
 *    Vulcano no puede manejar.»
 *
 * Nunca se mezclan en un mismo selector. Son dos preguntas distintas y un
 * manifiesto puede estar FACTURADO en Vulcano y SIN REVISAR aquí.
 */

/** Lo que dice Vulcano. Solo lectura: nunca lo tocamos. */
const ESTADOS_REMESA = [
  /* Trae los DOS estados vivos, así que el rótulo no puede nombrar solo uno. */
  { valor: '', texto: 'En la calle', clave: 'EN_TRANSITO' },
  { valor: 'CUMPLIDO', texto: 'Cumplido', clave: 'CUMPLIDO' },
  { valor: 'FACTURADO', texto: 'Facturado', clave: 'FACTURADO' },
  { valor: 'todos', texto: 'Cualquiera', clave: '' },
] as const

/** Lo NUESTRO: lo que pasa en la carretera y Vulcano no ve. */
const ESTADOS_VIAJE = [
  { valor: '', texto: 'Cualquiera', clave: '' },
  { valor: 'sin_tocar', texto: 'No ha abierto la app', clave: '' },
  { valor: 'arrancados', texto: 'Ya arrancó', clave: 'arrancados' },
  { valor: 'ENTREGADA', texto: 'Entregó, sin revisar', clave: 'ENTREGADA' },
  { valor: 'VALIDADA', texto: 'Entregó y ya se aprobó', clave: 'VALIDADA' },
  { valor: 'DEVUELTA', texto: 'La oficina la devolvió', clave: 'DEVUELTA' },
  { valor: 'DEVUELTA_A_PLANTA', texto: 'Devolvió la carga a planta', clave: 'DEVUELTA_A_PLANTA' },
] as const

export function Manifiestos() {
  const [faltantes, setFaltantes] = useState<Faltante[] | null>(null)
  /** Cuántos faltan de verdad: la lista trae LIMIT 500. */
  const [totalFaltantes, setTotalFaltantes] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [subiendo, setSubiendo] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [busca, setBusca] = useState('')
  /** Qué se está mirando: lo urgente o todo lo que falta. */
  const [urgencia, setUrgencia] = useState<'rodando' | 'todos' | 'loVe'>('todos')
  /** El estado del VIAJE. Lo pidió Luis con nombre propio. */
  const [estadoRemesa, setEstadoRemesa] = useState('')
  const [estadoViaje, setEstadoViaje] = useState('')
  const [desde, setDesde] = useState('')
  const [hasta, setHasta] = useState('')
  const [cuentaRemesa, setCuentaRemesa] = useState<Record<string, number>>({})
  const [cuentaViaje, setCuentaViaje] = useState<Record<string, number>>({})
  const [lote, setLote] = useState<Propuesta[] | null>(null)
  const [reconociendo, setReconociendo] = useState(false)
  const entradaLote = useRef<HTMLInputElement>(null)
  const [verSubidos, setVerSubidos] = useState(false)
  /** El manifiesto que se está mirando sin bajarlo. */
  const [viendo, setViendo] = useState<string | null>(null)
  /**
   * 🚨 Arrastrar los PDF a la pantalla. Lo pidió Luis: «lo de arrastrar los pdf
   * sí». Con 470 manifiestos por subir, abrir el explorador de archivos cada
   * vez es el cuello de botella de verdad.
   */
  const [arrastrando, setArrastrando] = useState(false)
  const [subidos, setSubidos] = useState<Subido[] | null>(null)
  /*
   * 🚨 El contador viene del servidor, NO de la lista de arriba.
   *
   * La lista solo se pide cuando alguien abre esa pestaña, así que contar sus
   * filas hacía que el número dijera «0» siempre. Medido: había 3 y mostraba 0.
   */
  const [yaSubidos, setYaSubidos] = useState(0)
  /** La fecha de corte de la app, si la oficina puso una. */
  const [corte, setCorte] = useState<string | null>(null)

  const cargar = useCallback(async () => {
    setError(null)
    try {
      // El filtro de estado y el de fecha van al SERVIDOR: son 5.010
      // manifiestos y traerlos todos para filtrar aquí sería mandarle varios
      // megas al logístico por gusto.
      const rango = desde && hasta ? `&desde=${desde}&hasta=${hasta}` : ''
      const r = await pedirConSesion<{
        faltantes: Faltante[]
        estadosDeRemesa: Record<string, number>
        estadosDeViaje: Record<string, number>
        yaSubidos: number
        totalFaltantes?: number
        corte: string | null
      }>(`/api/admin/manifiestos/faltantes?remesa=${estadoRemesa}&viaje=${estadoViaje}${rango}`)
      setFaltantes(r.faltantes)
      setTotalFaltantes(r.totalFaltantes ?? null)
      setCuentaRemesa(r.estadosDeRemesa ?? {})
      setCuentaViaje(r.estadosDeViaje ?? {})
      setYaSubidos(r.yaSubidos ?? 0)
      setCorte(r.corte ?? null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar')
    }
  }, [estadoRemesa, estadoViaje, desde, hasta])

  useEffect(() => {
    void cargar()
  }, [cargar])

  /*
   * 🚨 Los que ya se subieron, para poder REVISARLOS.
   *
   * Sin esta lista el ciclo quedaba abierto: se suben 30 PDF de una sola vez
   * —emparejados por el nombre del archivo— y no había forma de comprobar que
   * cada uno quedó donde debía. Un archivo mal emparejado no lo descubre nadie
   * hasta que a un conductor lo paran en un retén con el papel de otro camión.
   */
  useEffect(() => {
    if (!verSubidos || subidos !== null) return
    void (async () => {
      try {
        const r = await pedirConSesion<{ subidos: Subido[] }>('/api/admin/manifiestos/subidos')
        setSubidos(r.subidos)
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo cargar lo subido')
      }
    })()
  }, [verSubidos, subidos])

  async function subir(numero: string, archivo: File) {
    setSubiendo(numero)
    setAviso(null)
    setError(null)
    try {
      const dataUrl = await leerComoDataUrl(archivo)
      await pedirConSesion(`/api/admin/manifiestos/${encodeURIComponent(numero)}`, {
        method: 'POST',
        body: JSON.stringify({ archivo: dataUrl }),
      })
      setAviso(`Manifiesto ${numero} subido. El conductor ya lo puede bajar.`)
      await cargar()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo subir')
    } finally {
      setSubiendo(null)
    }
  }

  /*
   * 🚨 Se escogen los archivos y el servidor dice a qué manifiesto va cada uno,
   * mirando el nombre. Pero NO se sube nada todavía.
   *
   * Subirle a un conductor el manifiesto de otro camión es peor que no subirle
   * nada: mostraría en un retén un papel que no es el suyo. Por eso el
   * emparejamiento se muestra en pantalla y el logístico confirma.
   */
  /**
   * ⛔ Si algo falló, el titular NO puede decir solo cuántos entraron: la
   * oficina cerraría el panel creyendo que quedó todo hecho.
   */
  function resumenDelLote(l: typeof lote): string {
    if (!l) return ''
    const ok = l.filter((p) => p.estado === 'subido').length
    const mal = l.filter((p) => p.estado === 'error').length
    const uno = (n: number) => (n === 1 ? '1 manifiesto' : `${n} manifiestos`)
    if (mal === 0) return `Listos: ${uno(ok)} ${ok === 1 ? 'subido' : 'subidos'}`
    if (ok === 0) return `Ninguno se pudo subir: ${uno(mal)} con problema`
    return `${uno(ok)} ${ok === 1 ? 'subido' : 'subidos'} · ${mal} con problema`
  }

  /** «812 KB», «1,4 MB» — para que el tamaño se lea de un vistazo. */
  function peso(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`
  }

  /*
   * El lote TERMINÓ cuando ya no queda nada esperando turno y algo se intentó.
   * ⛔ Ojo: «terminó» no es «salió bien». Si fallaron TODOS también terminó, y
   * ese es justo el caso en que el panel no puede seguir diciendo «revisa
   * antes de subir» con un botón apagado de «Subir los 0».
   */
  const yaSubioTodo =
    !!lote &&
    lote.some((p) => p.estado === 'subido' || p.estado === 'error') &&
    !lote.some((p) => p.manifiesto && (p.estado === 'listo' || p.estado === 'subiendo'))

  async function reconocer(archivos: File[]) {
    setReconociendo(true)
    setError(null)
    setAviso(null)
    try {
      const r = await pedirConSesion<{
        reconocidos: {
          nombre: string
          manifiesto: string | null
          porque: Propuesta['porque']
          candidatos?: string[]
        }[]
      }>('/api/admin/manifiestos/reconocer', {
        method: 'POST',
        body: JSON.stringify({ nombres: archivos.map((a) => a.name) }),
      })
      setLote(
        archivos.map((archivo, i) => {
          const c = r.reconocidos[i]
          return {
            archivo,
            manifiesto: c?.manifiesto ?? null,
            porque: c?.porque ?? 'sin-coincidencia',
            ...(c?.candidatos ? { candidatos: c.candidatos } : {}),
            estado: 'listo' as const,
          }
        }),
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo leer la lista')
    } finally {
      setReconociendo(false)
    }
  }

  /*
   * Se suben de a tres. De uno en uno, cien archivos serían una eternidad; de
   * golpe, cien peticiones con un PDF cada una tumban al navegador y saturan el
   * servidor. Tres avanza rápido sin ahogar nada.
   */
  async function subirElLote() {
    if (!lote) return
    const porSubir = lote.filter((p) => p.manifiesto && p.estado === 'listo')
    if (porSubir.length === 0) return
    setError(null)

    const marcar = (archivo: File, cambio: Partial<Propuesta>) =>
      setLote((antes) =>
        (antes ?? []).map((p) => (p.archivo === archivo ? { ...p, ...cambio } : p)),
      )

    let i = 0
    const trabajador = async () => {
      while (i < porSubir.length) {
        const p = porSubir[i++]
        if (!p || !p.manifiesto) continue
        marcar(p.archivo, { estado: 'subiendo' })
        try {
          const dataUrl = await leerComoDataUrl(p.archivo)
          await pedirConSesion(`/api/admin/manifiestos/${encodeURIComponent(p.manifiesto)}`, {
            method: 'POST',
            body: JSON.stringify({ archivo: dataUrl }),
          })
          marcar(p.archivo, { estado: 'subido' })
        } catch (e) {
          marcar(p.archivo, {
            estado: 'error',
            mensaje: e instanceof Error ? e.message : 'No se pudo subir',
          })
        }
      }
    }
    await Promise.all([trabajador(), trabajador(), trabajador()])
    await cargar()
    // ⛔ No cantar victoria si algo falló: el panel de arriba ya lo detalla.
    setAviso(null)
  }

  /*
   * ⭐ Regla de la casa, dictada por Luis: **el cuadro de búsqueda no busca en
   * cada letra, sino cuando la persona hace una pequeña pausa.**
   *
   * Aquí importa igual que en la app del conductor: esta lista llega a tener
   * miles de manifiestos, y filtrarla en cada tecla obliga a repasarla y
   * repintarla entera. La caja de texto no espera; lo único que espera es la
   * lista.
   */
  const buscaReposada = useEsperaLaPausa(busca, 300)
  const lista = (faltantes ?? []).filter((f) => {
    /*
     * La pestaña «Rodando sin papel»: los que ya arrancaron **y siguen en la
     * calle**.
     *
     * 🚨 Antes bastaba con haber arrancado, y por eso mostraba 37 mientras el
     * tablero decía 12 — dos pantallas, la misma frase, números distintos. Un
     * manifiesto de una carga ya entregada no expone a nadie a una multa: el
     * viaje terminó.
     */
    if (urgencia === 'rodando' && !(f.ya_arranco && f.sigue_en_calle !== false)) return false
    /* La pestaña del corte: solo lo que algún conductor va a mirar. */
    if (urgencia === 'loVe' && f.lo_ve_el_conductor === false) return false
    const t = buscaReposada.trim().toLowerCase()
    if (!t) return true
    return (
      f.numero_manifiesto.toLowerCase().includes(t) ||
      (f.placas ?? '').toLowerCase().includes(t) ||
      (f.conductores ?? '').includes(t)
    )
  })

  /*
   * 🚨 Los indicadores. Luis: «¿Qué hay que hacer? ¿Los de hoy? ¿Lo que ya
   * está en viaje?» — se calculan sobre lo que llegó del servidor.
   */
  const hoyISO = new Date().toLocaleDateString('sv-SE')
  /* Mismo criterio que el filtro y que el tablero: arrancó Y sigue en calle. */
  const rodando = (faltantes ?? []).filter(
    (f) => f.ya_arranco && f.sigue_en_calle !== false,
  ).length
  /*
   * 🚨 Cuántos de los que faltan los ve TODAVÍA algún conductor.
   *
   * Con la fecha de corte puesta, más de la mitad de la lista puede ser de
   * viajes que ya nadie abre en la app. Medido el 21-ago: 256 de 499. Subir
   * esos papeles no le sirve a nadie hoy.
   */
  const losVeAlguien = (faltantes ?? []).filter((f) => f.lo_ve_el_conductor !== false).length
  const deHoy = (faltantes ?? []).filter((f) => (f.fecha_despacho ?? '').slice(0, 10) === hoyISO).length
  const viejos = (faltantes ?? []).filter((f) => Number(f.dias_esperando ?? 0) > 3).length

  const pag = usarPaginas(lista)

  /*
   * Se cuenta cuántas veces entra y sale el puntero: al pasar por encima de un
   * hijo el navegador dispara `dragleave` del padre, y sin contar, el marco
   * parpadearía todo el tiempo.
   */
  const profundidad = useRef(0)

  return (
    <div
      onDragEnter={(e) => {
        if (![...e.dataTransfer.types].includes('Files')) return
        e.preventDefault()
        profundidad.current += 1
        setArrastrando(true)
      }}
      onDragOver={(e) => {
        if ([...e.dataTransfer.types].includes('Files')) e.preventDefault()
      }}
      onDragLeave={() => {
        profundidad.current = Math.max(0, profundidad.current - 1)
        if (profundidad.current === 0) setArrastrando(false)
      }}
      onDrop={(e) => {
        e.preventDefault()
        profundidad.current = 0
        setArrastrando(false)
        if (!puedeCambiar()) return
        const archivos = [...e.dataTransfer.files].filter(
          (a) => a.type.includes('pdf') || a.type.startsWith('image/'),
        )
        if (archivos.length) void reconocer(archivos)
      }}
    >
      {/*
       * 🚨 Arrastrar los PDF. Lo pidió Luis: «lo de arrastrar los pdf sí».
       * El marco solo aparece mientras se arrastra algo encima.
       */}
      {arrastrando && puedeCambiar() && (
        <div className="soltar-aqui">
          <div>
            <b>Suelta aquí los manifiestos</b>
            <span>PDF o fotos. Se emparejan solos por el número del archivo.</span>
          </div>
        </div>
      )}

      <div className="tit-fila">
        <h1>Manifiestos</h1>
        <span>
          El papel que el conductor tiene que llevar. Sin él, un retén es multa.
        </span>
      </div>

      {/*
       * 🚨 LOS INDICADORES, que son la respuesta a lo que preguntó Luis:
       * «¿Qué hay que hacer? ¿Los de hoy? ¿Lo que ya está en viaje?»
       *
       * ⭐ El orden es por lo que cuesta, no por lo que es fácil de contar:
       * primero lo que puede costar una multa hoy mismo.
       */}
      <div className="kpis kpis-4">
        <article className="kpi">
          <p className="kpi-et">Rodando sin el papel</p>
          <p className="kpi-n num" style={rodando > 0 ? { color: '#CA2B2B' } : undefined}>
            {rodando}
          </p>
          <p className="kpi-pie">Ya arrancaron. Multa si los paran</p>
        </article>
        <article className="kpi">
          <p className="kpi-et">Despachados hoy</p>
          <p className="kpi-n num">{deHoy}</p>
          <p className="kpi-pie">Súbelos antes de que salgan</p>
        </article>
        <article className="kpi">
          <p className="kpi-et">Llevan más de 3 días</p>
          <p className="kpi-n num">{viejos}</p>
          <p className="kpi-pie">Se están quedando atrás</p>
        </article>
        <article className="kpi">
          <p className="kpi-et">Ya subidos</p>
          <p className="kpi-n num">{yaSubidos}</p>
          <p className="kpi-pie">Con su archivo cargado</p>
        </article>
      </div>

      {/*
       * ⛔⛔ EL ORDEN NO SE TOCA: título → indicadores → una caja → tabla.
       * Nada se mete entre los filtros y la tabla.
       */}
      <div className="filtros-barra">
        <div className="pestanas">
          <button
            onClick={() => { setVerSubidos(false); setUrgencia('rodando') }}
            className={`pestana ${!verSubidos && urgencia === 'rodando' ? 'pestana-on' : ''}`}
          >
            Rodando sin papel
            <b className="num">{rodando}</b>
          </button>
          {/* 🚨 Solo aparece si hay fecha de corte: sin corte, todos los ve
              alguien y una pestaña que repite el total sobra. */}
          {corte && (
            <button
              onClick={() => { setVerSubidos(false); setUrgencia('loVe') }}
              className={`pestana ${!verSubidos && urgencia === 'loVe' ? 'pestana-on' : ''}`}
            >
              El conductor lo ve
              <b className="num">{losVeAlguien}</b>
            </button>
          )}
          <button
            onClick={() => { setVerSubidos(false); setUrgencia('todos') }}
            className={`pestana ${!verSubidos && urgencia === 'todos' ? 'pestana-on' : ''}`}
          >
            Todos los que faltan
            {/*
              🚨 El total viene del servidor, no del largo de la lista: esa
              trae LIMIT 500. Medido el 24-ago-2026: decía «500» y hay 5.100.
            */}
            <b className="num">{fmt(totalFaltantes ?? (faltantes ?? []).length)}</b>
          </button>
          <button
            onClick={() => setVerSubidos(true)}
            className={`pestana ${verSubidos ? 'pestana-on' : ''}`}
          >
            Ya subidos
            {/* 🚨 Del servidor, no de la lista: esa solo se pide al abrir la
                pestaña, así que contarla decía «0» hasta que alguien entrara.
                Es el mismo fallo que ya se arregló en el indicador de arriba —
                estaba escrito dos veces. */}
            <b className="num">{yaSubidos}</b>
          </button>
        </div>

        <div className="filtros-fila">
          <div className="campo">
            <label htmlFor="m-desde">Despachado desde</label>
            <input id="m-desde" type="date" value={desde} max={hasta}
              onChange={(e) => setDesde(e.target.value)} />
          </div>
          <div className="campo">
            <label htmlFor="m-hasta">Hasta</label>
            <input id="m-hasta" type="date" value={hasta} min={desde}
              onChange={(e) => setHasta(e.target.value)} />
          </div>
          {/*
           * 🚨 EL FILTRO QUE PIDIÓ LUIS con nombre propio:
           *   «¿Dónde están los filtros por el estado del viaje? Los que ya
           *    fueron entregados, los que ya están en tránsito, los que están
           *    apenas en viaje.»
           * Cada opción dice cuántos hay, para no entrar a contar.
           */}
          {/*
           * ⛔ Los DOS ejes, cada uno con su rótulo. Nunca mezclados: es la
           * regla que Luis tuvo que repetirme varias veces.
           */}
          <div className="campo">
            <label htmlFor="m-est-remesa">Estado de la remesa</label>
            <select id="m-est-remesa" value={estadoRemesa} onChange={(e) => setEstadoRemesa(e.target.value)}>
              {ESTADOS_REMESA.map((x) => {
                const c = x.clave ? cuentaRemesa[x.clave] : undefined
                return (
                  <option key={x.texto} value={x.valor}>
                    {x.texto}{c != null ? ` (${c})` : ''}
                  </option>
                )
              })}
            </select>
          </div>

          <div className="campo">
            <label htmlFor="m-est-viaje">Estado del viaje</label>
            <select id="m-est-viaje" value={estadoViaje} onChange={(e) => setEstadoViaje(e.target.value)}>
              {ESTADOS_VIAJE.map((x) => {
                const c = x.clave ? cuentaViaje[x.clave] : undefined
                return (
                  <option key={x.texto} value={x.valor}>
                    {x.texto}{c != null ? ` (${c})` : ''}
                  </option>
                )
              })}
            </select>
          </div>

          <div className="campo campo-ancho">
            <label htmlFor="m-busca">Manifiesto, placa o cédula</label>
            <input id="m-busca" value={busca} onChange={(e) => setBusca(e.target.value)}
              placeholder="000044158 · TGA195 · 8793443" />
          </div>
          {(busca || desde || hasta || estadoRemesa || estadoViaje) && (
            <button type="button" className="btn-limpiar"
              onClick={() => { setBusca(''); setDesde(''); setHasta(''); setEstadoRemesa(''); setEstadoViaje('') }}>
              Limpiar
            </button>
          )}
          <div style={{ marginLeft: 'auto' }}>
          <input
            ref={entradaLote}
            type="file"
            accept="application/pdf,image/*"
            multiple
            className="hidden"
            aria-label="Escoger varios manifiestos"
            onChange={(e) => {
              const a = [...(e.target.files ?? [])]
              if (a.length) void reconocer(a)
              e.target.value = ''
            }}
          />
          <button
            onClick={() => entradaLote.current?.click()}
            disabled={reconociendo}
            className="btn-buscar"
          >
            {reconociendo ? 'Leyendo…' : 'Subir varios manifiestos'}
          </button>
          </div>
        </div>

        <p className="linea-msg">
          {verSubidos
            ? 'Ábrelos y comprueba que cada archivo sea el del camión correcto. Si uno quedó mal, súbelo otra vez: el anterior deja de usarse y el conductor ve el nuevo.'
            : 'Estos conductores están en la calle sin el papel. Si los paran en un retén, hay multa.'}
        </p>

        {/* Los atajos, cada uno diciendo qué fechas aplica. */}
        <div className="atajos">
          {atajosManifiesto().map((a) => {
            const puesto = desde === a.desde && hasta === a.hasta
            return (
              <button key={a.texto} type="button"
                className={`chip ${puesto ? 'chip-on' : ''}`}
                onClick={() => { setDesde(a.desde); setHasta(a.hasta) }}>
                {a.texto}
                <em>{a.cuando}</em>
              </button>
            )
          })}
        </div>
      </div>

      {/*
       * 🚨 El emparejamiento se REVISA antes de subir.
       *
       * Subirle a un conductor el manifiesto de otro camión es peor que no
       * subirle nada: mostraría en un retén un papel que no es el suyo. Aquí ve
       * qué archivo va a qué manifiesto, y los que no se reconocieron quedan
       * aparte para subirlos a mano desde la tabla de abajo.
       */}
      {lote && (
        <div className="mb-4 rounded-[4px] bg-white p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              {/*
               * 🚨 Cuando ya se subieron todos, el panel tiene que decir que
               * TERMINÓ. Se midió: quedaba diciendo «3 de 3 reconocidos ·
               * revisa antes de subir» con un botón «Subir los 0 reconocidos»,
               * apagado. Un botón que ofrece hacer nada deja a quien lo mira
               * pensando que algo salió mal.
               */}
              <p className="text-[16px] font-semibold">
                {yaSubioTodo ? resumenDelLote(lote) : `${lote.filter((p) => p.manifiesto).length} de ${lote.length} reconocidos por el nombre del archivo`}
              </p>
              <p className="mt-0.5 text-[14px] text-[#5A6A83]">
                {yaSubioTodo
                  ? lote.some((p) => p.estado === 'error')
                    ? 'Los que fallaron siguen pendientes: arréglalos y vuelve a subirlos.'
                    : 'Los conductores ya los pueden guardar en el celular para mostrarlos en un retén.'
                  : 'Revisa que cada archivo vaya al manifiesto correcto antes de subir.'}
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                onClick={() => setLote(null)}
                className="h-10 rounded-[4px] border border-[#CFD4DE] px-4 text-[14px] font-medium"
              >
                {yaSubioTodo ? 'Cerrar' : 'Cancelar'}
              </button>
              {!yaSubioTodo && (
                <button
                  onClick={() => void subirElLote()}
                  disabled={!lote.some((p) => p.manifiesto && p.estado === 'listo')}
                  className="h-10 rounded-[4px] bg-[#2a8553] px-4 text-[14px] font-medium text-white disabled:opacity-50"
                >
                  Subir los {lote.filter((p) => p.manifiesto && p.estado === 'listo').length}{' '}
                  reconocidos
                </button>
              )}
            </div>
          </div>

          <div className="mt-3 max-h-[320px] overflow-y-auto">
            {lote.map((p) => (
              <div
                key={p.archivo.name + p.archivo.size}
                className="border-b border-[#EDEFF3] py-2"
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="min-w-0 flex-1 truncate text-[14px]">{p.archivo.name}</span>
                  {/*
                   * 🚨 El peso a la vista ANTES de subir. Un escaneo bueno pesa
                   * cientos de KB; uno de 8 KB entre los demás salta a la vista
                   * sin tener que abrirlos uno por uno.
                   */}
                  <span
                    className={`num w-[72px] shrink-0 text-right text-[13px] ${
                      p.archivo.size < 3 * 1024 ? 'font-semibold text-[#CA2B2B]' : 'text-[#5A6A83]'
                    }`}
                  >
                    {peso(p.archivo.size)}
                  </span>
                  <span className="num w-[140px] shrink-0 text-[14px] font-medium">
                    {p.manifiesto ?? (
                      <span className="font-normal text-[#A4600D]">
                        {p.porque === 'ambiguo' ? 'coincide con varios' : 'no lo reconocí'}
                      </span>
                    )}
                  </span>
                  <span className="w-[90px] shrink-0 text-right text-[14px]">
                    {p.estado === 'subido' ? (
                      <span className="font-medium text-[#1F6E45]">Subido</span>
                    ) : p.estado === 'subiendo' ? (
                      <span className="text-[#5A6A83]">Subiendo…</span>
                    ) : p.estado === 'error' ? (
                      <span className="font-medium text-[#CA2B2B]">Falló</span>
                    ) : p.manifiesto ? (
                      <span className="text-[#5A6A83]">Listo</span>
                    ) : (
                      <span className="text-[#5A6A83]">—</span>
                    )}
                  </span>
                </div>
                {/*
                 * ⛔ El motivo NO puede quedarse en un tooltip: quien sube 200
                 * archivos no pasa el mouse por encima de cada uno. Si algo
                 * falló, tiene que leerse.
                 */}
                {p.estado === 'error' && p.mensaje && (
                  <p className="mt-0.5 text-[13px] text-[#CA2B2B]">{p.mensaje}</p>
                )}
                {p.estado !== 'error' && p.archivo.size < 3 * 1024 && (
                  <p className="mt-0.5 text-[13px] text-[#CA2B2B]">
                    Pesa muy poco para ser un manifiesto escaneado. Ábrelo antes de subirlo.
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {aviso && (
        <div className="mb-3 rounded-[4px] bg-[#E6F4EC] px-4 py-3 text-[14px] text-[#1F6E45]">
          {aviso}
        </div>
      )}
      {error && (
        <div className="mb-3 rounded-[4px] bg-[#FDECEC] px-4 py-3 text-[14px] text-[#CA2B2B]">
          {error}
        </div>
      )}

      {viendo && <VerManifiesto numero={viendo} alCerrar={() => setViendo(null)} />}

      {verSubidos && (
        <>
          {subidos === null && <p className="cargando">Buscando…</p>}
          {subidos !== null && subidos.length === 0 && (
            <p className="cargando">Todavía no se ha subido ninguno.</p>
          )}
          {/*
           * 🚨 La caja NO es adorno: sin ella la tabla queda suelta, las
           * columnas se pegan unas con otras y el peso se lee dentro de la
           * fecha. Luis lo vio así: «¿Cómo se te ocurre que lo que es ya subido
           * tiene que verse así? Esto es una mierda.» Y tenía razón: el
           * envoltorio se me había perdido en un reemplazo.
           */}
          {/*
            🚨 Los que no van a abrir en un retén, arriba y contados.
            Medido: 8 de 10 pesaban menos de 1 KB —subidos antes de que
            existiera el control de peso— y la pantalla decía «Ya subidos: 10»
            como si todo estuviera bien. El conductor se enteraría con el
            policía enfrente.
          */}
          {subidos !== null && subidos.some((m) => m.sirve === false) && (
            <div className="aviso-manifiestos-malos">
              <b>
                {subidos.filter((m) => m.sirve === false).length} de {subidos.length} no van a
                abrir en un retén
              </b>
              <span>
                Pesan muy poco para ser un escaneo. Ábrelos con «Abrirlo»: si no se ve el
                manifiesto, vuelve a subirlo — el que ya está no se borra, queda la versión nueva.
              </span>
            </div>
          )}

          {subidos !== null && subidos.length > 0 && (
            <div className="tabla-caja">
            <div className="tabla-scroll">
            <table>
              <thead>
                <tr>
                  <th>Manifiesto</th>
                  <th>Placa</th>
                  <th>Conductor</th>
                  <th>Peso</th>
                  <th>Subido</th>
                  <th style={{ textAlign: 'right' }}>Archivo</th>
                </tr>
              </thead>
              <tbody>
                {subidos.map((m) => (
                  <tr key={m.numero_manifiesto}>
                    <td className="num" style={{ fontWeight: 600 }}>{m.numero_manifiesto}</td>
                    <td className="num">{m.placas ?? '—'}</td>
                    <td className="num">{m.conductores ?? '—'}</td>
                    {/* Un archivo de 422 bytes no es «0 KB»: leído así parece
                        vacío y roto. Debajo de 1 KB se dice en bytes. */}
                    <td className="num">
                      <span style={m.sirve === false ? { color: '#CA2B2B', fontWeight: 600 } : undefined}>
                        {m.bytes < 1024 ? `${m.bytes} B` : `${Math.round(m.bytes / 1024)} KB`}
                      </span>
                      {m.sirve === false && (
                        <small style={{ display: 'block', color: '#CA2B2B' }}>no abre</small>
                      )}
                    </td>
                    <td>{fecha(m.subido_at)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {/*
                       * 🚨 Se trae con la sesión puesta, no con un enlace directo.
                       *
                       * Un `<a href>` no manda la cabecera de sesión, así que el
                       * enlace directo habría dado 401 y el botón no serviría para
                       * nada. Se pide con la sesión y se abre desde la memoria.
                       */}
                      <button
                        onClick={() => setViendo(m.numero_manifiesto)}
                        className="btn-tabla"
                      >
                        Abrirlo
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            </div>
          )}
        </>
      )}

      {!verSubidos && faltantes !== null && faltantes.length === 0 && (
        <div className="rounded-[4px] bg-white p-10 text-center">
          <p className="text-[16px] font-semibold text-[#1F6E45]">
            Todos los viajes en curso tienen manifiesto
          </p>
          <p className="mt-1 text-[14px] text-[#5A6A83]">Nada que subir por ahora.</p>
        </div>
      )}

      {/*
       * 🚨 Aquí caían las 470 filas de una sola vez: 24.961 px de alto, que son
       * treinta pantallas de scroll. Medido. Nadie baja treinta pantallas, y es
       * el mismo error que Luis ya había reclamado en Entregas.
       */}
      {!verSubidos && lista.length > 0 && (
        <div className="tabla-caja">
          <div className="tabla-scroll">
            <table>
              <thead>
                <tr>
                  <th>Manifiesto</th>
                  <th>Placa</th>
                  <th>Conductor</th>
                  <th>Remesas</th>
                  <th>Despacho</th>
                  <th>Estado de la remesa</th>
                  <th>Estado del viaje</th>
                  <th style={{ textAlign: 'right' }}>Subir</th>
                </tr>
              </thead>
              <tbody>
                {pag.enPantalla.map((f) => (
                  <tr key={f.numero_manifiesto}>
                    <td className="num" style={{ fontWeight: 600 }}>{f.numero_manifiesto}</td>
                    <td className="num">{f.placas ?? '—'}</td>
                    <td className="num">{f.conductores ?? '—'}</td>
                    <td className="num">{f.remesas}</td>
                    <td>
                      {fecha(f.fecha_despacho)}
                      {Number(f.dias_esperando ?? 0) > 0 && (
                        <small>
                          hace {Number(f.dias_esperando)}{' '}
                          {Number(f.dias_esperando) === 1 ? 'día' : 'días'}
                        </small>
                      )}
                    </td>
                    {/*
                      ⛔ Las dos columnas van SEPARADAS, nunca en una sola: son
                      dos preguntas distintas. Un manifiesto puede estar
                      facturado en Vulcano y con la entrega sin revisar aquí.
                    */}
                    <td>
                      <span className="et et-neu">
                        {estaEnLaCalle(f.estado_vulcano)
                          ? 'En la calle'
                          : f.estado_vulcano === 'CUMPLIDO'
                            ? 'Cumplido'
                            : f.estado_vulcano === 'FACTURADO'
                              ? 'Facturado'
                              : (f.estado_vulcano ?? '—')}
                      </span>
                    </td>

                    <td>
                      {/*
                       * 🚨 Rodando sin el papel es lo único rojo: es lo que
                       * puede costar una multa hoy (regla 23).
                       */}
                      {f.ya_arranco &&
                      !['ENTREGADA', 'ENTREGADA_CON_NOVEDAD', 'VALIDADA', 'DEVUELTA_A_PLANTA'].includes(
                        f.estado_evidencia ?? '',
                      ) ? (
                        <span className="et et-mal">Rodando sin el papel</span>
                      ) : f.estado_evidencia === 'DEVUELTA_A_PLANTA' ? (
                        <span className="et et-esp">Devolvió la carga</span>
                      ) : f.estado_evidencia === 'VALIDADA' ? (
                        <span className="et et-ok">Entregó y se aprobó</span>
                      ) : ['ENTREGADA', 'ENTREGADA_CON_NOVEDAD'].includes(f.estado_evidencia ?? '') ? (
                        <span className="et et-esp">Entregó, sin revisar</span>
                      ) : f.estado_evidencia === 'DEVUELTA' ? (
                        <span className="et et-esp">Se le devolvió</span>
                      ) : (
                        <span className="et et-neu">No ha abierto la app</span>
                      )}
                    </td>

                    <td style={{ textAlign: 'right' }}>
                      {puedeCambiar() ? (
                        <BotonSubir
                          numero={f.numero_manifiesto}
                          ocupado={subiendo === f.numero_manifiesto}
                          alEscoger={(a) => void subir(f.numero_manifiesto, a)}
                        />
                      ) : (
                        <span className="et et-neu">Solo lectura</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PieDeTabla {...pag} queSon="manifiestos por subir" />
        </div>
      )}
    </div>
  )
}

/** El botón de subir: un `input type=file` disfrazado, que es lo que hay. */
function BotonSubir({
  numero,
  ocupado,
  alEscoger,
}: {
  numero: string
  ocupado: boolean
  alEscoger: (archivo: File) => void
}) {
  const entrada = useRef<HTMLInputElement>(null)
  return (
    <>
      <input
        ref={entrada}
        type="file"
        accept="application/pdf,image/*"
        className="hidden"
        aria-label={`Subir el manifiesto ${numero}`}
        onChange={(e) => {
          const a = e.target.files?.[0]
          if (a) alEscoger(a)
          // Se limpia para que escoger el mismo archivo otra vez vuelva a disparar.
          e.target.value = ''
        }}
      />
      <button
        onClick={() => entrada.current?.click()}
        disabled={ocupado}
        className="h-9 rounded-[4px] bg-navy px-4 text-[14px] font-medium text-white disabled:opacity-50"
      >
        {ocupado ? 'Subiendo…' : 'Subir PDF'}
      </button>
    </>
  )
}

function leerComoDataUrl(archivo: File): Promise<string> {
  return new Promise((resolver, rechazar) => {
    const lector = new FileReader()
    lector.onload = () => resolver(String(lector.result))
    lector.onerror = () => rechazar(new Error('No se pudo leer el archivo'))
    lector.readAsDataURL(archivo)
  })
}

function fecha(iso: string | null): string {
  if (!iso) return '—'
  const f = new Date(iso)
  if (Number.isNaN(f.getTime())) return '—'
  return f.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * Abre el manifiesto en una pestaña nueva, con la sesión puesta.
 *
 * 🚨 La pestaña se abre ANTES de pedir el archivo: si se abriera después, el
 * navegador la bloquea como ventana emergente porque ya no la asocia al toque.
 * Se abre en blanco y se le pone el contenido cuando llega.
 */
/**
 * Trae el archivo del manifiesto **con la sesión puesta**.
 *
 * 🚨 Un `<iframe src="/api/...">` no manda la cabecera de sesión: el servidor
 * respondería 401 y saldría un marco en blanco. Por eso se pide con fetch y se
 * muestra desde la memoria del navegador.
 */
async function traerArchivo(numero: string): Promise<{ url: string; esPdf: boolean }> {
  const token = tokenGuardado()
  const r = await fetch(`/api/admin/manifiestos/${encodeURIComponent(numero)}/archivo`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!r.ok) throw new Error('No se pudo abrir el archivo')
  const blob = await r.blob()
  return { url: URL.createObjectURL(blob), esPdf: blob.type.includes('pdf') }
}

/**
 * El manifiesto, a la vista, sin bajarlo al computador.
 *
 * 🚨 Lo pidió Luis: *«ver el archivo sin descargarlo también»*. Antes se abría
 * en otra pestaña, que en la práctica es descargarlo: se pierde el hilo de lo
 * que se estaba revisando y toca volver.
 */
function VerManifiesto({ numero, alCerrar }: { numero: string; alCerrar: () => void }) {
  const [archivo, setArchivo] = useState<{ url: string; esPdf: boolean } | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let vivo = true
    let sueltaEsto: string | null = null
    void (async () => {
      try {
        const a = await traerArchivo(numero)
        if (!vivo) {
          URL.revokeObjectURL(a.url)
          return
        }
        sueltaEsto = a.url
        setArchivo(a)
      } catch (e) {
        if (vivo) setError(e instanceof Error ? e.message : 'No se pudo abrir')
      }
    })()
    return () => {
      vivo = false
      if (sueltaEsto) URL.revokeObjectURL(sueltaEsto)
    }
  }, [numero])

  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') alCerrar()
    }
    window.addEventListener('keydown', alTeclear)
    return () => window.removeEventListener('keydown', alTeclear)
  }, [alCerrar])

  return (
    <div className="visor-velo" onClick={alCerrar}>
      <div className="visor visor-ancho" onClick={(e) => e.stopPropagation()}>
        <div className="visor-cab">
          <div>
            <b className="num">{numero}</b>
            <span>Manifiesto</span>
          </div>
          <button onClick={alCerrar} aria-label="Cerrar" className="visor-x">
            &times;
          </button>
        </div>
        <div className="visor-cuerpo visor-cuerpo-alto">
          {error && <p className="visor-msg">{error}</p>}
          {!error && !archivo && <p className="visor-msg">Abriendo…</p>}
          {archivo &&
            (archivo.esPdf ? (
              <iframe src={archivo.url} title={`Manifiesto ${numero}`} className="visor-pdf" />
            ) : (
              <img src={archivo.url} alt={`Manifiesto ${numero}`} />
            ))}
        </div>
        <div className="visor-pie">
          <span>Compruébalo: tiene que ser el papel de este camión.</span>
        </div>
      </div>
    </div>
  )
}


/**
 * Devuelve lo que se escribió, pero **solo cuando se hizo una pausa**.
 *
 * ⭐ Regla de la casa para TODOS los cuadros de búsqueda, dictada por Luis:
 * «que no busque enseguida apenas uno coloque la letra que uno escriba; al
 * hacer una pequeña pausa, que busque».
 */
function useEsperaLaPausa<T>(valor: T, ms: number): T {
  const [reposado, setReposado] = useState(valor)
  useEffect(() => {
    const t = setTimeout(() => setReposado(valor), ms)
    return () => clearTimeout(t)
  }, [valor, ms])
  return reposado
}
